import type { SearchFilters } from '@/components/SearchFilters/types'
import { getNeighborhoodsFromClustersValues } from '@/utils/locations'
import type { LocationsQueryUtils } from '@/stores/locationsStore/types'
import { ESTIMATED_DELIVERY_TIMEFRAME } from '@/components/BrandedV2/FormSearchFilters/enums'
import { isUSCity } from '@/constants/us-cities'

export const formatFilterValue = (value: string | null | string[]) => {
  if (Array.isArray(value)) {
    return value.join('~')
  }

  return value
}

export const parseSearchFilters = (filters: {[key: string]: (string | null)}, countryCode: string, locationsQueryUtils: LocationsQueryUtils): SearchFilters => {
  const hasClustersSelected = !!filters.clusters

  const clusters = hasClustersSelected ? (formatFilterValue(filters.clusters) as string).split('~') : []
  const validNeighborhoodValues = locationsQueryUtils.getAllAvailableNeighborhoods().map(neighborhood => neighborhood.value)

  let businessHubs: string[]
  let neighborhoods: string[]
  const deliveryTerm: ESTIMATED_DELIVERY_TIMEFRAME[] = filters.deliveryTerm ? (filters.deliveryTerm.split('~') as ESTIMATED_DELIVERY_TIMEFRAME[]) : []
  const bedroomsNumber: string[] = filters.bedroomsNumber ? filters.bedroomsNumber.split('~') : []
  const bathroomsNumber: string[] = filters.bathroomsNumber ? filters.bathroomsNumber.split('~') : []
  const parkingLotsNumber: string[] = filters.parkingLotsNumber ? filters.parkingLotsNumber.split('~') : []

  if (filters.neighborhoods) {
    neighborhoods = (formatFilterValue(filters.neighborhoods) as string)
      .split('~')
      .filter(neighborhoodValue => validNeighborhoodValues.includes(neighborhoodValue))
    businessHubs = locationsQueryUtils.getBusinessHubsByNeighborhoodsValues(neighborhoods).map(hub => hub.value)
  } else if (hasClustersSelected) {
    neighborhoods = getNeighborhoodsFromClustersValues(clusters, countryCode)
      .map(neighborhood => neighborhood.value)
      .filter(neighborhoodValue => validNeighborhoodValues.includes(neighborhoodValue))
    businessHubs = locationsQueryUtils.getBusinessHubsByNeighborhoodsValues(neighborhoods).map(hub => hub.value)
  } else {
    neighborhoods = []
    businessHubs = []
  }

  // Special handling for US cities filter - convert to locations format
  const locations: import('@/components/SearchFilters/types').LocationFilters[] = []
  const isUSCityFilter = isUSCity(filters.city)

  if (isUSCityFilter) {
    locations.push({
      city: filters.city,
      type: 'city'
    })
  }

  return {
    businessHubs,
    businessHub: filters.businessHub ?? null,
    clusters,
    neighborhoods,
    opportunityType: filters.opportunityType ?? null,
    deliveryTerm,
    bedroomsNumber,
    bathroomsNumber,
    parkingLotsNumber,
    price: {
      min: parseInt(filters.price?.split(':')[0] as string) || null,
      max: parseInt(filters.price?.split(':')[1] as string) || null
    },
    area: {
      min: parseInt(filters.area?.split(':')[0] as string) || null,
      max: parseInt(filters.area?.split(':')[1] as string) || null
    },
    q: filters.q ?? null,
    ids: filters.ids?.split('~') ?? [],
    subsidies: filters.subsidies ? filters.subsidies.split('~') : null,
    locations: locations.length > 0 ? locations : undefined,
    housingCondition: filters.housingCondition ?? null
  }
}

export const convertSearchFiltersToQueryObject = (filters: SearchFilters) => {
  const query: any = {}

  if (filters.businessHubs?.length) {
    query.business_hubs = filters.businessHubs.join('~')
  }

  if (filters.clusters?.length) {
    query.clusters = filters.clusters.join('~')
  }

  if (filters.neighborhoods?.length) {
    query.neighborhoods = filters.neighborhoods.join('~')
  }

  if (filters.opportunityType) {
    query.opportunity_type = filters.opportunityType
  }

  if (filters.deliveryTerm?.length) {
    query.delivery_term = formatFilterValue(filters.deliveryTerm)
  }

  if (filters.bedroomsNumber?.length) {
    query.bedrooms_number = formatFilterValue(filters.bedroomsNumber)
  }

  if (filters.bathroomsNumber?.length) {
    query.bathrooms_number = formatFilterValue(filters.bathroomsNumber)
  }

  if (filters.parkingLotsNumber?.length) {
    query.parking_lots_number = formatFilterValue(filters.parkingLotsNumber)
  }

  if (filters.price) {
    if (Object.values(filters.price).some(Boolean)) {
      query.price = `${filters.price.min || ''}:${filters.price.max || ''}`
    } else {
      query.price = undefined
    }
  }

  if (filters.area) {
    if (Object.values(filters.area).some(Boolean)) {
      query.area = `${filters.area.min || ''}:${filters.area.max || ''}`
    } else {
      query.area = undefined
    }
  }

  if (filters.q) {
    query.q = filters.q
  }

  if (filters.ids?.length) {
    query.ids = filters.ids.join('~')
  }

  if (filters.locations?.length) {
    const zones = new Set<string>()
    const cities = new Set<string>()

    filters.locations.forEach((location) => {
      if (location.zone) zones.add(location.zone)
      if (location.city) cities.add(location.city)
    })

    if (zones.size) query.zone = formatFilterValue([...zones])
    if (cities.size) query.city = formatFilterValue([...cities])
  }

  if (filters.subsidies?.length) {
    query.subsidies = filters.subsidies.join('~')
  }

  if (filters.housingCondition) {
    query.housing_condition = filters.housingCondition
  }

  return query
}

export const extractValidFilters = (filters: SearchFilters) => {
  const validFilters: Partial<SearchFilters> = {}

  if (filters.businessHubs?.length) {
    validFilters.businessHubs = filters.businessHubs
  }

  if (filters.clusters?.length) {
    validFilters.clusters = filters.clusters
  }

  if (filters.neighborhoods?.length) {
    validFilters.neighborhoods = filters.neighborhoods
  }

  if (filters.opportunityType) {
    validFilters.opportunityType = filters.opportunityType
  }

  if (filters.deliveryTerm?.length) {
    validFilters.deliveryTerm = filters.deliveryTerm
  }

  if (filters.bedroomsNumber?.length) {
    validFilters.bedroomsNumber = filters.bedroomsNumber
  }

  return validFilters
}
